// Pure computation logic for the Activation Funnel.
// Extracted from ActivationFunnelTile.tsx so it can be unit-tested.

export interface FunnelData {
    webVisits: number;
    icpSignups: number;
    signupReasonNotNull: number;
    onboardingStarted: number;
    scheduleGenerated: number;
    schedulePublished: number;
    activated: number;
    history?: {
        webVisits: { current: number; prevWeek: number | null; avg6Weeks: number | null };
        icpSignups: { current: number; prevWeek: number | null; avg6Weeks: number | null };
        signupReasonNotNull: { current: number; prevWeek: number | null; avg6Weeks: number | null };
        onboardingStarted: { current: number; prevWeek: number | null; avg6Weeks: number | null };
        scheduleGenerated: { current: number; prevWeek: number | null; avg6Weeks: number | null };
        schedulePublished: { current: number; prevWeek: number | null; avg6Weeks: number | null };
        activated: { current: number; prevWeek: number | null; avg6Weeks: number | null };
    };
}

export type DataKey = keyof FunnelData | "activatedToPaid";

export interface StepConfig {
    key: string;
    title: string;
    subtitle: (pct: number, prevCount?: number, count?: number) => string;
    dataKey: DataKey;
    prevKey?: keyof FunnelData;
    targetPercent: number | null;
    goalOverride?: number;
    gradient: [string, string];
    isNorthStar?: boolean;
}

export interface StepComputed {
    count: number | string;
    rate: number;
    barWidth: number;
    targetLeft: number | null;
    goalCount: number | null;
    vsPct: number | null;
    dropPct: number;
    dropNote: string;
    dropSeverity: "sev" | "ok";
    prevWeekValue?: number | null;
    avg6WeekValue?: number | null;
}

export const TARGET_SIGNUP = 3;
export const TARGET_ONBOARDING = 70;
export const TARGET_ONBOARDING_STARTED = 80;
export const TARGET_ACTIVATION = 40;
export const TARGET_PAID = 30;
export const TARGET_OVERALL = 0.25;

export const FUNNEL_STEPS: StepConfig[] = [
    {
        key: "webVisits",
        title: "Unique Web Visits",
        subtitle: () => "Top of funnel",
        dataKey: "webVisits",
        targetPercent: null,
        goalOverride: 500,
        gradient: ["#3d8bfd", "#60a5fa"],
    },
    {
        key: "signups",
        title: "Signups",
        subtitle: (pct) => `Conversion ${pct}%`,
        dataKey: "icpSignups",
        prevKey: "webVisits",
        targetPercent: TARGET_SIGNUP,
        gradient: ["#6366f1", "#818cf8"],
    },
    {
        key: "accountSetup",
        title: "Account Setup",
        subtitle: (pct, _prev, count) => (count !== undefined ? `${pct}% of signups` : `${pct}%`),
        dataKey: "signupReasonNotNull",
        prevKey: "icpSignups",
        targetPercent: TARGET_ONBOARDING,
        gradient: ["#8b5cf6", "#a78bfa"],
    },
    {
        key: "onboarding",
        title: "First Event Created",
        subtitle: (pct, _prev, count) => (count !== undefined ? `${pct}% of setup` : `${pct}%`),
        dataKey: "onboardingStarted",
        prevKey: "signupReasonNotNull",
        targetPercent: TARGET_ONBOARDING_STARTED,
        gradient: ["#a855f7", "#c084fc"],
    },
    {
        key: "activated",
        title: "Activated",
        subtitle: (pct, _prev, count) => (count !== undefined ? `${pct}% of onboarding` : `${pct}%`),
        dataKey: "activated",
        prevKey: "onboardingStarted",
        targetPercent: TARGET_ACTIVATION,
        gradient: ["var(--green)", "var(--green-t)"],
    },
    {
        key: "scheduleGenerated",
        title: "Schedule Generated",
        subtitle: (pct, _prev, count) => (count !== undefined ? `${pct}% of onboarding` : `${pct}%`),
        dataKey: "scheduleGenerated",
        prevKey: "onboardingStarted",
        targetPercent: null,
        gradient: ["#a855f7", "#c084fc"],
    },
    {
        key: "schedulePublished",
        title: "Schedule Published ★",
        subtitle: () => "North star metric",
        dataKey: "schedulePublished",
        prevKey: "scheduleGenerated",
        targetPercent: TARGET_ACTIVATION,
        gradient: ["var(--green)", "var(--green-t)"],
        isNorthStar: true,
    },
    {
        key: "paid",
        title: "Activated to Paid",
        subtitle: () => "Revenue Event",
        dataKey: "activatedToPaid",
        prevKey: "activated",
        targetPercent: TARGET_PAID,
        gradient: ["var(--green)", "var(--green-t)"],
    },
];

export const HISTORY_KEY_BY_STEP_KEY: Record<string, keyof NonNullable<FunnelData["history"]> | null> = {
    webVisits: "webVisits",
    signups: "icpSignups",
    accountSetup: "signupReasonNotNull",
    onboarding: "onboardingStarted",
    activated: "activated",
    scheduleGenerated: "scheduleGenerated",
    schedulePublished: "schedulePublished",
    paid: null,
};

export function computeStepValues(
    step: StepConfig,
    funnel: FunnelData,
    activatedToPaid: number,
    total: number,
    maxGoal: number,
    goalOverrides: Record<string, number> = {},
    signupScale?: number,
    prevStepGoal?: number | null,
    paidAbsoluteCount?: number
): StepComputed {
    const getCount = (): number | string => {
        if (step.dataKey === "activatedToPaid") return paidAbsoluteCount ?? 0;
        const raw = funnel[step.dataKey as keyof FunnelData] as unknown;
        return typeof raw === "number" || typeof raw === "string" ? raw : 0;
    };
    const count = getCount();
    const prevCount =
        step.prevKey && step.prevKey !== "history"
            ? ((funnel[step.prevKey as Exclude<keyof FunnelData, "history">] as unknown) as number | undefined) ?? 0
            : total;
    const actualNum = typeof count === "number" ? count : 0;

    let rate: number;

    if (step.key === "webVisits") {
        rate = 100;
    } else if (step.key === "signups") {
        rate = total ? Math.round((funnel.icpSignups / total) * 100) : 0;
    } else if (step.key === "accountSetup") {
        rate = funnel.icpSignups ? Math.round((funnel.signupReasonNotNull / funnel.icpSignups) * 100) : 0;
    } else if (step.key === "onboarding") {
        rate = funnel.signupReasonNotNull ? Math.round((funnel.onboardingStarted / funnel.signupReasonNotNull) * 100) : 0;
    } else if (step.key === "activated") {
        rate = funnel.onboardingStarted ? Math.round((funnel.activated / funnel.onboardingStarted) * 100) : 0;
    } else if (step.key === "scheduleGenerated") {
        rate = funnel.onboardingStarted ? Math.round((funnel.scheduleGenerated / funnel.onboardingStarted) * 100) : 0;
    } else if (step.key === "schedulePublished") {
        rate = funnel.scheduleGenerated ? Math.round((funnel.schedulePublished / funnel.scheduleGenerated) * 100) : 0;
    } else {
        rate = Math.round(activatedToPaid);
    }

    let goalCount: number | null = null;
    let vsPct: number | null = null;

    if (step.goalOverride != null) {
        goalCount = step.goalOverride;
    } else if (step.targetPercent !== null && prevStepGoal != null && prevStepGoal > 0) {
        goalCount = Math.round(prevStepGoal * (step.targetPercent / 100));
    }

    if (goalCount !== null) {
        vsPct = goalCount > 0 ? Math.round((actualNum / goalCount) * 100) : 0;
    }

    const userGoal = goalOverrides[step.key];
    if (userGoal !== undefined && userGoal !== null && typeof userGoal === "number") {
        goalCount = userGoal;
        vsPct = userGoal > 0 ? Math.round((actualNum / userGoal) * 100) : 0;
    }

    let barWidth: number;
    let targetLeft: number | null = null;
    const scale = signupScale != null && signupScale > 0 ? signupScale : maxGoal;
    if (step.key === "webVisits") {
        barWidth = 100;
    } else if (step.key === "signups" && goalCount != null && scale > 0) {
        if (actualNum < goalCount) {
            barWidth = Math.max((actualNum / goalCount) * 100, actualNum > 0 ? 0.5 : 0);
            targetLeft = 100;
        } else {
            barWidth = 100;
            targetLeft = actualNum > 0 ? (goalCount / actualNum) * 100 : null;
        }
    } else if (scale > 0) {
        barWidth = Math.max((actualNum / scale) * 100, actualNum > 0 ? 0.5 : 0);
        targetLeft =
            goalCount != null ? Math.min((goalCount / scale) * 100, 100) : null;
    } else {
        barWidth = 0;
        targetLeft = goalCount != null ? 100 : null;
    }

    const dropPct = step.key === "webVisits" ? 0 : 100 - rate;
    let dropNote = "";
    if (step.key === "signups") {
        dropNote = `${prevCount - (typeof count === "number" ? count : 0)} didn't sign up`;
    } else if (step.key === "accountSetup") {
        dropNote = `${prevCount - (typeof count === "number" ? count : 0)} didn't finish setup`;
    } else if (step.key === "onboarding") {
        const c = typeof count === "number" ? count : 0;
        dropNote = c > 0 ? `${prevCount - c} didn't start onboarding` : "None started onboarding";
    } else if (step.key === "activated") {
        const c = typeof count === "number" ? count : 0;
        dropNote = c > 0 ? `${prevCount - c} didn't activate` : "None activated";
    } else if (step.key === "scheduleGenerated") {
        const c = typeof count === "number" ? count : 0;
        dropNote = c > 0 ? `All ${c} generated a schedule` : "No one generated a schedule";
    } else if (step.key === "schedulePublished") {
        const c = typeof count === "number" ? count : 0;
        dropNote = c > 0 ? `${prevCount - c} didn't publish` : "No one published";
    } else if (step.key === "paid") {
        dropNote = `${100 - rate}% didn't convert to paid`;
    }
    const dropSeverity: "sev" | "ok" = dropPct === 0 ? "ok" : "sev";

    const historyKey = HISTORY_KEY_BY_STEP_KEY[step.key];
    const historyForStep =
        historyKey && funnel.history
            ? (funnel.history[historyKey] as { current: number; prevWeek: number | null; avg6Weeks: number | null })
            : undefined;

    return {
        count,
        rate,
        barWidth,
        targetLeft,
        goalCount,
        vsPct,
        dropPct,
        dropNote,
        dropSeverity,
        prevWeekValue: historyForStep?.prevWeek ?? null,
        avg6WeekValue: historyForStep?.avg6Weeks ?? null,
    };
}

/**
 * Run the full goal cascade over all FUNNEL_STEPS, returning per-step computed values
 * and the effective goal map. This is the same logic used by ActivationFunnelTile
 * in its two-pass computation.
 */
export function computeFunnelCascade(
    funnel: FunnelData,
    activatedToPaid: number,
    paidAbsoluteCount: number,
    goalOverrides: Record<string, number> = {},
    signupScale?: number,
): { steps: Array<{ step: StepConfig; computed: StepComputed }>; goalByStepKey: Record<string, number | null> } {
    const dataKeyToStepKey: Record<string, string> = {};
    for (const s of FUNNEL_STEPS) dataKeyToStepKey[s.dataKey as string] = s.key;

    const total = funnel.webVisits || 1;
    const goalByStepKey: Record<string, number | null> = {};
    const steps: Array<{ step: StepConfig; computed: StepComputed }> = [];

    for (const step of FUNNEL_STEPS) {
        const parentStepKey = step.prevKey ? dataKeyToStepKey[step.prevKey] : undefined;
        const parentGoal = parentStepKey != null ? (goalByStepKey[parentStepKey] ?? null) : null;
        const computed = computeStepValues(
            step, funnel, activatedToPaid, total, 1, goalOverrides,
            signupScale, parentGoal, paidAbsoluteCount,
        );
        steps.push({ step, computed });
        goalByStepKey[step.key] = computed.goalCount;
    }

    return { steps, goalByStepKey };
}
